// ============================================================================
// FIDÉLITÉ — le programme, réglé depuis le back-office.
//
// UTILITÉ : la caisse édite ces réglages depuis longtemps ; le back-office ne les voyait pas. Or c'est
// lui qui a la vue d'ensemble — ce qui reste en circulation, combien de clients portent une cagnotte,
// ce qui a été crédité et consommé.
//
// ⚠️ LES RÉGLAGES RESTENT ÉDITABLES DES DEUX CÔTÉS (décision utilisateur 2026-08-31). Conséquence
// assumée, dite à l'écran : sans arbitrage, le dernier écrivain gagne — un administrateur en caisse
// peut écraser ce que le back-office vient de poser, et réciproquement.
//
// ⚠️ UNE CLÉ ABSENTE N'EST PAS UNE CLÉ VIDE. Le serveur ne rend que ce qui est réellement enregistré ;
// l'écran affiche alors le défaut de la caisse et le DIT, plutôt que de faire croire à une valeur
// posée que personne n'a choisie (§23.0).
//
// ⚠️ LES QUATRE MESURES DU HAUT SONT MESURÉES, jamais estimées, et « — » quand la donnée n'est pas
// connue : 0 dirait « j'ai regardé, il n'y a rien », ce qui est faux sur une base sans grand livre.
// ============================================================================
import { useCallback, useEffect, useMemo, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, showToast, Switch, Trans, useLatest, type IconName } from '@orgazone/ui-web';
import { currentCurrency, formatMoney } from '@orgazone/money-format';
import { FID_TIER_DEFAULTS, switchOn, type FidTier, type SwitchName } from '@orgazone/sync-contract';
import { api } from '../api';
import { useStoreScope } from '../store-context';
import { t } from '../i18n';

/**
 * Les défauts des VALEURS de la caisse — recopiés ici pour pouvoir DIRE « valeur par défaut », pas pour décider.
 * ⚠️ LES INTERRUPTEURS N'Y SONT PLUS : leur défaut se lit au contrat (`switchOn`), comme la caisse le lit.
 *    Recopiés ici, ils affichaient au siège un état que rien n'obligeait à rester celui de la caisse.
 */
const DEFAUTS: Record<string, string> = {
  'fid.mode': 'voucher',
  'fid.conversion': '5.0',
  'fid.welcome_bonus': '5',
  'fid.use_from': '1.00',
  'fid.voucher_threshold': '0',
  // Les deux durées des paliers sont au contrat, comme le barème : le serveur calcule avec elles.
  'fid.window_days': FID_TIER_DEFAULTS.window_days,
  'fid.protect_days': FID_TIER_DEFAULTS.protect_days,
};

/** Un palier, tel que la caisse le range en JSON sous `fid.tiers`. */
/**
 * LES PALIERS PAR DÉFAUT VIENNENT DU CONTRAT, ils ne se recopient pas.
 *
 * 🔴 CET ÉCRAN EN PORTAIT SA PROPRE COPIE, identique au caractère près à celle de
 *    `sync-contract/settings-catalog` — deux tables du même fait (Forme A). Le jour où le siège en
 *    change une, la caisse et le back-office ne proposent plus les mêmes paliers par défaut, et rien
 *    ne le signale : les deux écrans affichent chacun une liste cohérente avec elle-même.
 *
 * ⚠️ UN PALIER EST UNE DONNÉE, PAS UN TEXTE. Son libellé part en base sous `fid.tiers` (l'écran
 *    réécrit l'objet ENTIER à chaque saisie de borne) et le contrat le déclare remplaçable par le
 *    back-office. Une chaîne qui sort du compilateur pour aller en base ne se traduit pas : la
 *    traduire ferait diverger ce qui est STOCKÉ de ce qui est AFFICHÉ, et deux magasins de langues
 *    différentes n'enregistreraient pas la même donnée (Forme J). C'est pour cela que « Silver » ne
 *    passe par aucun résolveur, ici comme au contrat.
 * ⚠️ AUCUN DES DEUX ÉCRANS NE LAISSE SAISIR CE LIBELLÉ aujourd'hui — ni celui-ci, qui n'édite que
 *    les bornes et le taux, ni celui de la caisse. Le jour où l'un d'eux l'ouvrira, ce sera le mot
 *    DU COMMERÇANT, et la règle ci-dessus vaudra doublement.
 */
const PALIERS_DEFAUT: FidTier[] = [...FID_TIER_DEFAULTS.tiers];

/**
 * Un montant, DANS LA DEVISE DU MAGASIN.
 *
 * 🔴 IL ÉCRIVAIT « € » EN DUR. `formatEur` a été renommé `formatMoney` précisément
 *    parce qu'un rendu qui affiche « 1 250 CHF » sous un nom d'euro est l'écart qu'on refuse à
 *    l'écran — 290 sites remplacés mécaniquement. Celui-ci a survécu **parce qu'il n'appelait pas la
 *    fonction du tout** : il refaisait le rendu à la main, avec la devise ET le format de nombre en
  *    dur. Un remplacement mécanique ne voit pas les sites qui contournent ce qu’il remplace.
 * ⚠️ ET LE MONTANT RESTE EN CENTIMES : `formatMoney` prend l’unité mineure entière, donc le `/100`
  *    écrit à la main disparaît avec lui (« argent = centimes entiers partout »).
 */
const money = (c: number | null) => (c == null ? '—' : formatMoney(c, { thousandsSep: ' ' }));
const nb = (n: number | null) => (n == null ? '—' : n.toLocaleString('fr-FR'));

export function Fidelite() {
  const scope = useStoreScope();
  const gln = scope.view?.active?.gln ?? null;
  const [base, setBase] = useState<Record<string, string> | null>(null);
  const [brouillon, setBrouillon] = useState<Record<string, string>>({});
  const [mesures, setMesures] = useState<{
    circulationCents: number | null; cardHolders: number | null;
    creditedCents30d: number | null; redeemedCents30d: number | null;
  } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // ⚠️ SEULE LA DERNIÈRE LECTURE REMPLIT LE BROUILLON : les réglages de l'ancien magasin, revenus après ceux du
  //    nouveau, s'afficheraient sous son bandeau — et « Enregistrer » les écrirait dans sa base.
  const latest = useLatest();
  const load = useCallback(() => {
    const isLatest = latest();
    return api.loyaltySettings()
      .then((r) => { if (!isLatest()) return; setBase(r.settings); setBrouillon(r.settings); setMesures(r.mesures); setErr(null); })
      .catch((e) => { if (isLatest()) setErr((e as Error).message); });
  }, [latest]);
  // Le magasin de la session relance la lecture : `load` ne le lit pas, le serveur le connaît par la session.
  useEffect(() => { void load(); }, [gln, load]);

  /** La valeur AFFICHÉE : le brouillon, sinon la base, sinon le défaut de la caisse. */
  const v = (k: string) => brouillon[k] ?? DEFAUTS[k] ?? '';
  /** L'état AFFICHÉ d'un interrupteur : le brouillon, sinon la base, sinon le défaut du contrat. */
  const bool = (k: SwitchName) => switchOn(k, brouillon[k]);
  const set = (k: string, val: string) => setBrouillon((b) => ({ ...b, [k]: val }));
  const setBool = (k: SwitchName, val: boolean) => set(k, val ? '1' : '0');
  /** ⚠️ Dit si la valeur affichée vient d'un réglage ENREGISTRÉ ou du défaut — l'écran ne doit pas
   *  laisser croire que tout a été choisi. */
  const estDefaut = (k: string) => base != null && !(k in base) && !(k in brouillon);

  const paliers: FidTier[] = useMemo(() => {
    try {
      const brut = brouillon['fid.tiers'];
      return brut ? (JSON.parse(brut) as FidTier[]) : PALIERS_DEFAUT;
    } catch {
      // ⚠️ Un JSON illisible n'autorise pas à inventer : on montre les paliers par défaut et on le dit
      //    dans la note de la section, plutôt que d'écraser silencieusement ce que la caisse a écrit.
      return PALIERS_DEFAUT;
    }
  }, [brouillon]);
  const paliersIllisibles = useMemo(() => {
    const brut = brouillon['fid.tiers'];
    if (!brut) return false;
    try { JSON.parse(brut); return false; } catch { return true; }
  }, [brouillon]);

  const setPalier = (id: string, champ: 'from' | 'to' | 'rate', val: string) =>
    set('fid.tiers', JSON.stringify(paliers.map((p) => (p.id === id ? { ...p, [champ]: val } : p))));

  // ⚠️ « MODIFIÉ » SE MESURE PAR COMPARAISON, pas par un drapeau posé au premier clic : revenir à la
  //    valeur d'origine doit faire disparaître l'avertissement, sinon il ment.
  const modifie = base != null && Object.keys(brouillon).some((k) => brouillon[k] !== base[k])
    || (base != null && Object.keys(base).some((k) => brouillon[k] !== base[k]));

  const save = async () => {
    if (!base || busy) return;
    setBusy(true);
    try {
      // On n'envoie QUE ce qui a changé : réécrire l'identique produirait des événements sans contenu.
      const delta: Record<string, string> = {};
      for (const [k, val] of Object.entries(brouillon)) if (val !== base[k]) delta[k] = val;
      const r = await api.saveLoyaltySettings(delta);
      setBase(r.settings); setBrouillon(r.settings);
      showToast(t('Programme de fidélité enregistré — il descendra sur les caisses à leur prochaine synchronisation.'), 'ok', 7000);
    } catch (e) {
      showToast(t('Enregistrement refusé : {v}', { v: e instanceof Error ? e.message : String(e) }), 'err', 9000);
    } finally { setBusy(false); }
  };

  // ⚠️ L'erreur PASSE AVANT le chargement : sinon un endpoint HS laisse l'écran sur « Chargement… »
  //    pour toujours, l'erreur posée mais jamais rendue.
  // ⚠️ EN LIGNE, pas de classe : le back-office n'embarque aucun vocabulaire utilitaire (audit CSS).
  if (err) {
    return (
      <div style={{ padding: '12px 15px', borderRadius: 'var(--r-sm)', background: 'var(--rose-bg)', color: 'var(--rose-fg)', fontSize: 'var(--t-body)', lineHeight: 1.5 }}>
        {t('Programme de fidélité indisponible : {v}', { v: err })}
      </div>
    );
  }
  if (!base) return <div style={{ color: 'var(--ink-500)' }}>{t('Chargement du programme…')}</div>;

  const actif = bool('fid.enabled');
  const cashback = v('fid.mode') === 'cashback';

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 16, marginBottom: 18, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 250 }}>
          <h1 style={{ margin: 0, fontSize: 'var(--t-h1)', fontWeight: 700, letterSpacing: '-0.025em', color: 'var(--ink-900)' }}>{t('Fidélité')}</h1>
          {/* ⚠️ LE TEXTE DIT LE VRAI PARTAGE D'AUTORITÉ. La maquette écrivait « la caisse lit — le
              back-office décide » ; c'est la CIBLE, pas l'état actuel : les réglages restent éditables
              des deux côtés (décision 2026-08-31), et le dernier écrivain gagne. L'écrire ici évite
              qu'un exploitant croie ses valeurs protégées d'une modification en caisse. */}
          <p style={{ margin: '3px 0 0', fontSize: 'var(--t-title)', color: 'var(--ink-500)' }}>
            <Trans k='Ces réglages descendent sur les caisses du magasin. Ils restent <1>modifiables en caisse</1> par un administrateur : la dernière modification l’emporte, d’où qu’elle vienne.' parts={[(x) => <strong style={{ color: 'var(--ink-700)' }}>{x}</strong>]} /></p>
        </div>
        {modifie && (
          <span style={{
            height: 26, display: 'inline-flex', alignItems: 'center', padding: '0 10px', borderRadius: 'var(--r-pill)',
            background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-note)', fontWeight: 700,
          }}>{t('Modifications non enregistrées')}</span>
        )}
        <button
          type="button" className="btn-cta" disabled={!modifie || busy}
          onClick={() => { void save(); }}
          style={{ height: 40, padding: '0 17px', borderRadius: 'var(--r-sm)' }}
        >
          {busy ? t('Enregistrement…') : t('Enregistrer')}
        </button>
      </div>

      {/* LES QUATRE MESURES — toutes tirées du grand livre, « — » quand la donnée n'est pas connue. */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 12, marginBottom: 18 }}>
        <Kpi label={t('Cagnotte en circulation')} valeur={money(mesures?.circulationCents ?? null)} />
        <Kpi label={t('Clients porteurs')} valeur={nb(mesures?.cardHolders ?? null)} />
        <Kpi label={t('Crédités (30 j)')} valeur={money(mesures?.creditedCents30d ?? null)} teinte="var(--teal-700)" />
        <Kpi label={t('Consommés (30 j)')} valeur={money(mesures?.redeemedCents30d ?? null)} />
      </div>

      <Section>
        <Rangee
          heading={t('Programme de fidélité')}
          desc={t('Activer le cumul des gains et les récompenses')}
          defaut={estDefaut('fid.enabled')}
        >
          <Switch checked={actif} onChange={(x) => setBool('fid.enabled', x)} label="" />
        </Rangee>
      </Section>

      {/* ⚠️ TOUT LE PARAMÉTRAGE EST MASQUÉ QUAND LE PROGRAMME EST COUPÉ : offrir des réglages qui
          n'agissent sur rien donnerait à croire qu'ils s'appliquent. */}
      {!actif ? (
        <p style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', marginTop: 14 }}>
          
          {t('Le programme est désactivé : aucun gain n’est cumulé et aucune récompense n’est restituée. Les réglages réapparaîtront à sa réactivation — ils ne sont pas perdus.')}
        </p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 14 }}>
          <Section>
            <EnteteSection heading={t('Mode de fonctionnement')} desc={t('Comment les gains sont restitués au client quand il atteint son plafond')} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(240px,1fr))', gap: 12, padding: '0 18px 16px' }}>
              {/* ⚠️ LES MÊMES ICÔNES QUE LA CAISSE (`wallet` / `receipt`) et la même vignette :
                  l'opérateur qui règle le programme au back-office et celui qui le lit en caisse
                  regardent le MÊME objet — deux dessins différents pour un même choix demandent de
                  réapprendre l'écran à chaque changement de surface. */}
              <Choix
                actif={cashback} onClick={() => set('fid.mode', 'cashback')}
                icone="wallet"
                heading={t('Cashback')}
                desc={t('Gains déduits directement. Impression du bon désactivée, cumul permanent activé.')}
              />
              <Choix
                actif={!cashback} onClick={() => set('fid.mode', 'voucher')}
                icone="receipt"
                heading={t('Bon d’achat')}
                desc={t('Impression d’un bon à l’atteinte du plafond. Cumul des gains en option.')}
              />
            </div>
          </Section>

          <Section>
            <EnteteSection heading={t('Comportement au dépassement du plafond')} />
{/* ⚠️ EN CASHBACK, CES DEUX RÉGLAGES NE DÉCIDENT PLUS RIEN : le cumul est permanent et il n'y a
                pas de bon à imprimer. On montre la valeur EFFECTIVE sous cadenas plutôt qu'un
                interrupteur qui garderait sa position sans effet — c'est la solution de la caisse. */}
            <Rangee
              heading={t('Cumul des gains')}
              desc={cashback ? t('Toujours actif en mode cashback') : t('Le client peut choisir de continuer à accumuler plutôt qu’imprimer le bon')}
              defaut={!cashback && estDefaut('fid.allow_cumul')}
            >
              {cashback
                ? <PastilleVerrouillee texte={t('Activé automatiquement')} />
                : <Switch checked={bool('fid.allow_cumul')} onChange={(x) => setBool('fid.allow_cumul', x)} label="" />}
            </Rangee>
            <Rangee
              heading={t('Impression du bon d’achat')}
              desc={cashback ? t('Désactivée par le mode cashback') : t('Imprimer automatiquement le bon à l’atteinte du plafond')}
              defaut={!cashback && estDefaut('fid.print_voucher')}
            >
              {cashback
                ? <PastilleVerrouillee texte={t('Désactivée')} />
                : <Switch checked={bool('fid.print_voucher')} onChange={(x) => setBool('fid.print_voucher', x)} label="" />}
            </Rangee>
            <Rangee heading={t('Interdire l’utilisation immédiate des gains')} desc={t('Le client doit attendre la prochaine visite')} defaut={estDefaut('fid.forbid_immediate')}>
              <Switch checked={bool('fid.forbid_immediate')} onChange={(x) => setBool('fid.forbid_immediate', x)} label="" />
            </Rangee>
          </Section>

          <Section>
            <EnteteSection heading={t('Réglages des gains')} />
{/* ⚠️ PALIERS ACTIFS ⇒ PLUS DE TAUX DIRECT (retour utilisateur) : laisser saisir un pourcentage
                que le moteur ignore n'a aucun sens, et donne à croire qu'il s'applique. */}
            {bool('fid.tiers_on') ? (
              <Rangee heading={t('Conversion carte fidélité')} desc={t('Défini par palier ci-dessous — le taux principal est ignoré')}>
                <PastilleVerrouillee texte={t('Défini par les paliers')} />
              </Rangee>
            ) : (
              <Champ heading={t('Conversion carte fidélité')} suffixe="%" valeur={v('fid.conversion')} onChange={(x) => set('fid.conversion', x)}
                desc={t('Pourcentage du montant d’achat crédité à la cagnotte')} defaut={estDefaut('fid.conversion')} />
            )}
            <Champ heading={t('Bonus de bienvenue')} suffixe={currentCurrency().symbol} valeur={v('fid.welcome_bonus')} onChange={(x) => set('fid.welcome_bonus', x)} defaut={estDefaut('fid.welcome_bonus')} />
{cashback ? (
              <Rangee heading={t('Utilisation des gains à partir de')} desc={t('Sans objet en cashback — les gains se déduisent dès le premier euro')}>
                <PastilleVerrouillee texte={t('Aucun minimum')} />
              </Rangee>
            ) : (
              <>
              <Champ heading={t('Utilisation des gains à partir de')} suffixe={currentCurrency().symbol} valeur={v('fid.use_from')} onChange={(x) => set('fid.use_from', x)}
                defaut={estDefaut('fid.use_from')} />
              {/* ⚠️ DEUX SEUILS, PAS UN. Celui du dessus dit à partir de quand le client PEUT utiliser
                  ses gains ; celui-ci dit à partir de quand la cagnotte DEVIENT un bon. La caisse
                  confondait les deux et vidait la cagnotte à chaque vente (corrigé 2026-09-06). */}
              <Champ heading={t('Plafond d’émission du bon')} suffixe={currentCurrency().symbol} valeur={v('fid.voucher_threshold')} onChange={(x) => set('fid.voucher_threshold', x)}
                defaut={estDefaut('fid.voucher_threshold')} />
              </>
            )}
            {/* ⚠️ Le libellé DIT le sens du défaut : créditer une ligne déjà remisée revient à payer
                deux fois le même geste commercial. Le cas inverse existe, il se choisit. */}
            <Rangee
              heading={t('Gains sur articles remisés')}
              desc={bool('fid.earn_on_discounted')
                ? t('Les lignes en promotion, solde ou remise créditent la cagnotte.')
                : t('Les lignes en promotion, solde ou remise ne créditent pas la cagnotte (recommandé).')}
              defaut={estDefaut('fid.earn_on_discounted')}
            >
              <Switch checked={bool('fid.earn_on_discounted')} onChange={(x) => setBool('fid.earn_on_discounted', x)} label="" />
            </Rangee>
          </Section>

          <Section>
            <EnteteSection heading={t('Paliers clients (Silver / Gold / Platinium)')} />
            <Rangee heading={t('Paliers actifs')} desc={t('Un taux de conversion par palier, calculé sur le cumul d’achats')} defaut={estDefaut('fid.tiers_on')}>
              <Switch checked={bool('fid.tiers_on')} onChange={(x) => setBool('fid.tiers_on', x)} label="" />
            </Rangee>
            {bool('fid.tiers_on') && (
              <>
                <Champ heading={t('Fenêtre de calcul')} suffixe="jours" valeur={v('fid.window_days')} onChange={(x) => set('fid.window_days', x)}
                  desc={t('Montant cumulé sur les N derniers jours glissants')} defaut={estDefaut('fid.window_days')} />

                {paliersIllisibles && (
                  <div style={{ margin: '0 18px 12px', fontSize: 'var(--t-body)', color: 'var(--rose-fg)', lineHeight: 1.5 }}>
                    
                    {t('⚠️ Les paliers enregistrés sont illisibles (JSON invalide). Ceux affichés sont les paliers par défaut : enregistrer les remplacera.')}
                  </div>
                )}
                <div style={{ overflowX: 'auto' }}>
                  <div style={{ minWidth: 520, padding: '0 18px 4px' }}>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 110px', gap: 10, padding: '8px 0', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>
                      <span>{t('Palier')}</span><span>{t('De ({code})', { code: currentCurrency().code })}</span><span>{t('À ({code})', { code: currentCurrency().code })}</span><span>{t('Conversion (%)')}</span>
                    </div>
                    {paliers.map((p, i) => (
                      <div key={p.id} style={{ display: 'grid', gridTemplateColumns: '1fr 110px 110px 110px', gap: 10, padding: '7px 0', alignItems: 'center', borderTop: '1px solid var(--ink-100)' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--t-body)', fontWeight: 600, color: 'var(--ink-900)' }}>
                          <span style={{ width: 10, height: 10, borderRadius: 'var(--r-pill)', background: p.color }} />
                          {p.l}
                        </span>
                        <Saisie valeur={p.from} onChange={(x) => setPalier(p.id, 'from', x)} label={t('{palier} — de ({code})', { palier: p.l, code: currentCurrency().code })} />
                        {/* ⚠️ Le dernier palier n'a PAS de borne haute — « ∞ » le dit plutôt qu'un champ
                            vide qu'on prendrait pour un oubli. */}
                        {i === paliers.length - 1
                          ? <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)', paddingLeft: 4 }}>∞</span>
                          : <Saisie valeur={p.to} onChange={(x) => setPalier(p.id, 'to', x)} label={t('{palier} — à ({code})', { palier: p.l, code: currentCurrency().code })} />}
                        <Saisie valeur={p.rate} onChange={(x) => setPalier(p.id, 'rate', x)} label={t('{palier} — conversion (%)', { palier: p.l })} />
                      </div>
                    ))}
                  </div>
                </div>

                <Rangee heading={t('Protection du statut (anti-rétrogradation)')} desc={t('Le statut reste garanti après un passage de palier')} defaut={estDefaut('fid.keep_tier')}>
                  <Switch checked={bool('fid.keep_tier')} onChange={(x) => setBool('fid.keep_tier', x)} label="" />
                </Rangee>
                {bool('fid.keep_tier') && (
                  <Champ heading={t('Durée de protection')} suffixe="jours" valeur={v('fid.protect_days')} onChange={(x) => set('fid.protect_days', x)} defaut={estDefaut('fid.protect_days')} />
                )}
              </>
            )}
          </Section>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// PRIMITIVES DE L'ÉCRAN — géométrie relevée sur la maquette, pas inventée.
// ---------------------------------------------------------------------------

function Kpi({ label, valeur, teinte }: { label: string; valeur: string; teinte?: string }) {
  return (
    <div style={{ background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-sm)', padding: '14px 16px' }}>
      <div style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--t-micro)', letterSpacing: '.07em', textTransform: 'uppercase', color: 'var(--ink-500)' }}>{label}</div>
      <div className="tnum" style={{ fontSize: 'var(--t-feature)', fontWeight: 800, letterSpacing: '-0.02em', color: teinte ?? 'var(--ink-900)', marginTop: 4 }}>{valeur}</div>
    </div>
  );
}

const carte: CSSProperties = { background: 'var(--paper)', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-md)', overflow: 'hidden' };
function Section({ children }: { children: ReactNode }) { return <div style={carte}>{children}</div>; }

function EnteteSection({ heading, desc }: { heading: string; desc?: string }) {
  return (
    <div style={{ padding: '16px 18px 12px' }}>
      <div style={{ fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</div>
      {desc && <div style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>{desc}</div>}
    </div>
  );
}

/** Une rangée « libellé + explication + commande ». `defaut` marque une valeur jamais choisie. */
function Rangee({ heading, desc, defaut, children }: { heading: string; desc?: string; defaut?: boolean; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderTop: '1px solid var(--ink-100)' }}>
      <span style={{ flex: 1, minWidth: 220 }}>
        <span style={{ display: 'block', fontSize: 'var(--t-strong)', fontWeight: 700, color: 'var(--ink-900)' }}>
          {heading}{defaut && <MarqueDefaut />}
        </span>
        {desc && <span style={{ display: 'block', fontSize: 'var(--t-dense)', color: 'var(--ink-500)', marginTop: 2 }}>{desc}</span>}
      </span>
      {children}
    </div>
  );
}

function Champ({ heading, desc, suffixe, valeur, onChange, defaut }: {
  heading: string; desc?: string; suffixe: string; valeur: string; onChange: (v: string) => void; defaut?: boolean;
}) {
  return (
    <Rangee heading={heading} desc={desc} defaut={defaut}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7 }}>
        <Saisie valeur={valeur} onChange={onChange} label={heading} />
        <span style={{ fontSize: 'var(--t-body)', color: 'var(--ink-500)' }}>{suffixe}</span>
      </span>
    </Rangee>
  );
}

function Saisie({ valeur, onChange, label }: { valeur: string; onChange: (v: string) => void; label: string }) {
  return (
    <input
      aria-label={label} value={valeur} onChange={(e) => onChange(e.target.value)} inputMode="decimal"
      style={{
        width: 96, height: 34, padding: '0 10px', border: '1px solid var(--ink-300)', borderRadius: 'var(--r-ctrl)',
        background: 'var(--paper)', color: 'var(--ink-900)', fontFamily: 'var(--font-mono)', fontSize: 'var(--t-body)', textAlign: 'right',
      }}
    />
  );
}

/**
 * ⚠️ MARQUE « VALEUR PAR DÉFAUT ». Sans elle, un écran rempli laisse croire que tout a été choisi —
 * alors qu'une clé jamais enregistrée affiche simplement le défaut de la caisse.
 */
function MarqueDefaut() {
  return (
    <span
      title={t('Aucune valeur enregistrée : c’est le défaut de la caisse qui s’applique.')}
      style={{
        marginLeft: 8, fontSize: 'var(--t-micro)', fontWeight: 700, letterSpacing: '.04em', textTransform: 'uppercase',
        color: 'var(--ink-500)', background: 'var(--ink-100)', borderRadius: 'var(--r-pill)', padding: '2px 7px',
      }}
    >{t('par défaut')}</span>
  );
}

/**
 * Une vignette de choix — GÉOMÉTRIE RELEVÉE SUR LA CAISSE (`ChoiceTile`, variante `big`) : pastille
 * 40×40 rayon 10, bordure 1,5 px, coche à droite quand la vignette est retenue.
 * ⚠️ Reprise telle quelle, et pas approchée : c'est le même choix, montré sur deux surfaces.
 */
/**
 * PASTILLE VERROUILLÉE — la valeur EFFECTIVE quand un autre réglage décide à la place.
 *
 * ⚠️ MIEUX QU'UN CHAMP GRISÉ, et c'est tout l'intérêt : un champ inerte laisse voir une valeur qui
 * n'est PAS celle qui s'applique — l'écran ment alors sans rien dire de faux. La pastille montre ce
 * qui s'applique vraiment, et le cadenas dit pourquoi on ne peut pas y toucher.
 * ⚠️ Géométrie reprise de `LockedPill` (caisse) : h28, rayon plein, cadenas 13, texte 12/600.
 */
function PastilleVerrouillee({ texte }: { texte: string }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, height: 28, padding: '0 12px',
      borderRadius: 'var(--r-pill)', background: 'var(--ink-100)', color: 'var(--ink-700)', fontSize: 'var(--t-dense)', fontWeight: 600,
    }}>
      <Icon name="lock" size={13} color="var(--ink-600)" />
      {texte}
    </span>
  );
}

function Choix({ actif, onClick, icone, heading, desc }: {
  actif: boolean; onClick: () => void; icone: IconName; heading: string; desc: string;
}) {
  return (
    <button
      type="button" onClick={onClick}
      style={{
        textAlign: 'left', padding: 18, borderRadius: 14, cursor: 'pointer', display: 'flex',
        flexDirection: 'column', gap: 10,
        border: `1.5px solid ${actif ? 'var(--teal-500)' : 'var(--ink-200)'}`,
        background: actif ? 'var(--teal-100)' : 'var(--paper)',
      }}
    >
      <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 40, height: 40, borderRadius: 'var(--r-ctrl)', flexShrink: 0,
          background: actif ? 'var(--teal-500)' : 'var(--ink-100)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon name={icone} size={20} color={actif ? '#fff' : 'var(--ink-600)'} />
        </span>
        <span style={{ flex: 1, fontSize: 'var(--t-h4)', fontWeight: 700, color: 'var(--ink-900)' }}>{heading}</span>
        {/* La coche dit « c'est celui-là » sans dépendre de la seule couleur de fond. */}
        {actif && <Icon name="check" size={18} stroke={2.5} color="var(--teal-700)" />}
      </span>
      <span style={{ fontSize: 'var(--t-dense)', color: 'var(--ink-500)', lineHeight: 1.45 }}>{desc}</span>
    </button>
  );
}
